import React, { useContext, useMemo, useState } from "react";
import Table from "../../../../ui/widgets/table";
import { PAGE_SIZE } from "../../types";
import TableBody from "./body";
import TableFilter from "./filter";
import TableHeader from "./header";
import TablePager from "./pager";
import { comparator, sort } from "./sort";
import { SortDirection, TableProps } from "./types";
import { ProductContext } from "../../../../context/product-context/productContext";
import {
  CardholderTableRow,
  useCardholderTableBodyRows,
  useCardholderTableHeadRows,
} from "./body/rowGenerator";
import { ProductFamily } from "../../../../context/product-context/product-family";
import { Product } from "../../../../context/product-context/product";
import { ProductRefType } from "../../../../context/product-context/types";
import DashboardHelper from "../../helper";

export type FilterProductRefType = ProductRefType | "any";

export default function CustomTable({
  cardholderApplications,
}: TableProps): null | React.JSX.Element {
  const { selectedProduct } = useContext(ProductContext);

  const [order, setOrder] = useState<SortDirection>("asc");
  const [orderBy, setOrderBy] = useState<keyof CardholderTableRow>();
  const [filterByProduct, setFilterByProduct] =
    useState<FilterProductRefType>("any");
  const [filterByFamily, setFilterByFamily] = useState<ProductFamily>(() => {
    return DashboardHelper.getFamilyWithCompletedProducts(
      selectedProduct?.family,
      cardholderApplications,
    );
  });
  const [pageNumber, setPageNumber] = useState<number>(0);

  const headRows = useCardholderTableHeadRows();
  const columnNames = headRows.map((row) => row.id);
  const rows = useCardholderTableBodyRows(cardholderApplications);
  const filteredRows = useMemo(() => {
    return () =>
      rows.filter((val: CardholderTableRow) => {
        const isRowPartOfFamily =
          filterByFamily.products.find((product: Product) => {
            return val.productCode === product.productRef;
          }) !== undefined;
        const isRowPartOfProductType =
          filterByProduct === "any"
            ? true
            : val.productCode === filterByProduct;
        return isRowPartOfFamily && isRowPartOfProductType;
      });
  }, [filterByFamily, filterByProduct, rows]);

  if (rows === null) {
    return null;
  }

  const handleSortRequest = (
    _: React.MouseEvent,
    rowId: keyof CardholderTableRow,
  ) => {
    const isDesc = orderBy === rowId && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(rowId);
  };

  const rowsToDisplay = sort(filteredRows(), comparator(order, orderBy)).slice(
    pageNumber * PAGE_SIZE,
    pageNumber * PAGE_SIZE + PAGE_SIZE,
  );

  return (
    <>
      <TableFilter
        cardholderApplications={cardholderApplications}
        filterByFamily={filterByFamily}
        setFilterByFamily={setFilterByFamily}
        filterByProduct={filterByProduct}
        setFilterByProduct={setFilterByProduct}
      />
      <Table>
        <TableHeader
          orderBy={orderBy}
          order={order}
          rows={headRows}
          onSortChange={handleSortRequest}
        />
        <TableBody columns={columnNames} rows={rowsToDisplay} />
        <TablePager
          pageNumber={pageNumber}
          count={filteredRows().length}
          onPageNumberChange={setPageNumber}
        />
      </Table>
    </>
  );
}
